import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/toast';
import { useDeleteAccount } from '@/features/settings/hooks';
import { useAuth } from '@/providers/AuthProvider';

const CONFIRM_WORD = 'DELETAR';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeleteAccountDialog({ open, onOpenChange }: Props) {
  const [step, setStep] = useState<1 | 2>(1);
  const [typed, setTyped] = useState('');
  const deleteAccount = useDeleteAccount();
  const toast = useToast();
  const { logout } = useAuth();

  const reset = () => {
    setStep(1);
    setTyped('');
  };

  const handleOpenChange = (value: boolean) => {
    if (!value) reset();
    onOpenChange(value);
  };

  const handleConfirm = async () => {
    try {
      const res = await deleteAccount.mutateAsync();
      toast.show({
        title: 'Conta excluída.',
        description: `Seus dados serão apagados em ${new Date(res.purgeScheduledAt).toLocaleDateString('pt-BR')}.`,
        variant: 'success',
      });
      handleOpenChange(false);
      logout();
    } catch (err) {
      toast.show({
        title: 'Erro ao excluir',
        description: err instanceof Error ? err.message : 'Tente novamente.',
        variant: 'error',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Excluir conta</DialogTitle>
          <DialogDescription>
            Ação irreversível. Dados fiscais serão mantidos por 5 anos por obrigação legal.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {step === 1 ? (
            <div className="flex flex-col gap-3 text-sm">
              <p>
                Ao excluir sua conta:
              </p>
              <ul className="ml-4 list-disc space-y-1 text-(--color-fg-muted)">
                <li>Você não conseguirá fazer login.</li>
                <li>Emissões de NFS-e e envios automáticos são suspensos.</li>
                <li>Após 30 dias, seus dados pessoais (nome, e-mail, CRM, telefone) são apagados.</li>
                <li>Dados fiscais permanecem por 5 anos conforme exigência legal.</li>
              </ul>
              <p className="pt-2">
                Tem certeza que deseja continuar?
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <Label htmlFor="confirm">
                Digite <span className="font-mono font-bold">{CONFIRM_WORD}</span> para confirmar:
              </Label>
              <Input
                id="confirm"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={CONFIRM_WORD}
                autoComplete="off"
              />
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancelar
          </Button>
          {step === 1 ? (
            <Button variant="destructive" onClick={() => setStep(2)}>
              Entendi, prosseguir
            </Button>
          ) : (
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={typed !== CONFIRM_WORD || deleteAccount.isPending}
            >
              {deleteAccount.isPending ? 'Excluindo…' : 'Excluir minha conta'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
